"use client";

import { ContentBlock } from '@/features/editor/utils/content-blocks';

interface BlockEditorProps {
  block: ContentBlock;
  onUpdate: (id: string, block: ContentBlock) => void;
}

export function BlockEditor({ block, onUpdate }: BlockEditorProps) {
  const updateBlock = (updates: Partial<ContentBlock>) => {
    const blockId = block.id || crypto.randomUUID();
    const updatedBlock = { ...block, ...updates, id: blockId } as ContentBlock;
    onUpdate(blockId, updatedBlock);
  };

  switch (block.type) {
    case 'heading':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heading Level
            </label>
            <select
              value={block.level}
              onChange={(e) => updateBlock({ level: parseInt(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6 })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={1}>H1 - Main Title</option>
              <option value={2}>H2 - Section Title</option>
              <option value={3}>H3 - Subsection</option>
              <option value={4}>H4 - Minor Heading</option>
              <option value={5}>H5 - Small Heading</option>
              <option value={6}>H6 - Smallest Heading</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heading Text
            </label>
            <input
              type="text"
              value={block.text}
              onChange={(e) => updateBlock({ text: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Enter heading text..."
            />
          </div>
        </div>
      );

    case 'paragraph':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Paragraph Text
          </label>
          <textarea
            value={block.text}
            onChange={(e) => updateBlock({ text: e.target.value })}
            className="w-full p-2 border border-gray-300 rounded-md text-sm h-24 resize-none"
            placeholder="Enter paragraph text..."
          />
        </div>
      );

    case 'image':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <input
              type="url"
              value={block.src}
              onChange={(e) => updateBlock({ src: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="https://example.com/image.jpg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Alt Text
            </label>
            <input
              type="text"
              value={block.alt}
              onChange={(e) => updateBlock({ alt: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Describe the image..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Caption (optional)
            </label>
            <input
              type="text"
              value={block.caption || ''}
              onChange={(e) => updateBlock({ caption: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Image caption..."
            />
          </div>
        </div>
      );

    case 'video':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform
            </label>
            <select
              value={block.platform}
              onChange={(e) => updateBlock({ platform: e.target.value as 'cloudflare' | 'local' })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="cloudflare">Cloudflare (default)</option>
              <option value="local">Local Video/GIF</option>
            </select>
          </div>
          {block.platform === 'cloudflare' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cloudflare Stream UID
              </label>
              <input
                type="text"
                value={block.url}
                onChange={(e) => updateBlock({ url: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="e.g. abc123def456..."
              />
            </div>
          )}
          {block.platform === 'local' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Local Video/GIF URL
              </label>
              <input
                type="url"
                value={block.url}
                onChange={(e) => updateBlock({ url: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="/videos/example.mp4 or /images/example.gif"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title (optional)
            </label>
            <input
              type="text"
              value={block.title || ''}
              onChange={(e) => updateBlock({ title: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Video title..."
            />
          </div>
        </div>
      );

    case 'podcast':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Platform
            </label>
            <select
              value={block.platform}
              onChange={(e) => updateBlock({ platform: e.target.value as 'spotify' | 'apple' | 'custom' })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="spotify">Spotify</option>
              <option value="apple">Apple Podcasts</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Embed URL
            </label>
            <input
              type="url"
              value={block.embedUrl}
              onChange={(e) => updateBlock({ embedUrl: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="https://open.spotify.com/embed/episode/..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title (optional)
            </label>
            <input
              type="text"
              value={block.title || ''}
              onChange={(e) => updateBlock({ title: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Podcast title..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={block.description || ''}
              onChange={(e) => updateBlock({ description: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm h-16 resize-none"
              placeholder="Podcast description..."
            />
          </div>
        </div>
      );

    case 'quote':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quote Text
            </label>
            <textarea
              value={block.text}
              onChange={(e) => updateBlock({ text: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm h-20 resize-none"
              placeholder="Enter the quote..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Author Name
            </label>
            <input
              type="text"
              value={block.author}
              onChange={(e) => updateBlock({ author: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Author name..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Author Title (optional)
            </label>
            <input
              type="text"
              value={block.title || ''}
              onChange={(e) => updateBlock({ title: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Author title..."
            />
          </div>
        </div>
      );

    case 'statistics':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Layout
            </label>
            <select
              value={block.layout}
              onChange={(e) => updateBlock({ layout: e.target.value as 'grid' | 'carousel' | 'outcome-cards' })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="grid">Grid</option>
              <option value="carousel">Carousel</option>
              <option value="outcome-cards">Outcome Cards</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statistics
            </label>
            <div className="space-y-2">
              {block.stats.map((stat, index) => (
                <div key={index} className="p-2 border border-gray-200 rounded-md">
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input
                      type="text"
                      value={stat.value}
                      onChange={(e) => {
                        const newStats = [...block.stats];
                        newStats[index] = { ...stat, value: e.target.value };
                        updateBlock({ stats: newStats });
                      }}
                      className="p-1 border border-gray-300 rounded text-sm"
                      placeholder="Value (e.g., 95%)"
                    />
                    <input
                      type="text"
                      value={stat.title || ''}
                      onChange={(e) => {
                        const newStats = [...block.stats];
                        newStats[index] = { ...stat, title: e.target.value };
                        updateBlock({ stats: newStats });
                      }}
                      className="p-1 border border-gray-300 rounded text-sm"
                      placeholder="Title"
                    />
                  </div>
                  <input
                    type="text"
                    value={stat.description || ''}
                    onChange={(e) => {
                      const newStats = [...block.stats];
                      newStats[index] = { ...stat, description: e.target.value };
                      updateBlock({ stats: newStats });
                    }}
                    className="w-full p-1 border border-gray-300 rounded text-sm"
                    placeholder="Description"
                  />
                </div>
              ))}
              <button
                onClick={() => {
                  const newStats = [...block.stats, { value: '', title: '', description: '' }];
                  updateBlock({ stats: newStats });
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + Add Statistic
              </button>
            </div>
          </div>
        </div>
      );

    case 'bullet-list':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              List Items
            </label>
            <div className="space-y-2">
              {block.items.map((item, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => {
                      const newItems = [...block.items];
                      newItems[index] = e.target.value;
                      updateBlock({ items: newItems });
                    }}
                    className="flex-1 p-1 border border-gray-300 rounded text-sm"
                    placeholder="List item..."
                  />
                  <button
                    onClick={() => {
                      const newItems = block.items.filter((_, i) => i !== index);
                      updateBlock({ items: newItems });
                    }}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newItems = [...block.items, ''];
                  updateBlock({ items: newItems });
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                + Add Item
              </button>
            </div>
          </div>
        </div>
      );

    case 'cta-button':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Button Text
            </label>
            <input
              type="text"
              value={block.text}
              onChange={(e) => updateBlock({ text: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Button text..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link URL
            </label>
            <input
              type="url"
              value={block.href}
              onChange={(e) => updateBlock({ href: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="/book-a-demo"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Variant
              </label>
              <select
                value={block.variant || 'primary'}
                onChange={(e) => updateBlock({ variant: e.target.value as 'primary' | 'outline' | 'ghost' })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="primary">Primary</option>
                <option value="outline">Outline</option>
                <option value="ghost">Ghost</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Size
              </label>
              <select
                value={block.size || 'lg'}
                onChange={(e) => updateBlock({ size: e.target.value as 'sm' | 'md' | 'lg' })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="sm">Small</option>
                <option value="md">Medium</option>
                <option value="lg">Large</option>
              </select>
            </div>
          </div>
        </div>
      );

    case 'spacer':
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Spacer Height
          </label>
          <select
            value={block.height}
            onChange={(e) => updateBlock({ height: e.target.value as 'sm' | 'md' | 'lg' | 'xl' })}
            className="w-full p-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="sm">Small (16px)</option>
            <option value="md">Medium (32px)</option>
            <option value="lg">Large (64px)</option>
            <option value="xl">Extra Large (96px)</option>
          </select>
        </div>
      );

    case 'blog-block':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={block.title || ''}
              onChange={(e) => updateBlock({ title: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Block title..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subtitle
            </label>
            <input
              type="text"
              value={block.subtitle || ''}
              onChange={(e) => updateBlock({ subtitle: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Block subtitle..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body Text
            </label>
            <textarea
              value={block.bodyText || ''}
              onChange={(e) => updateBlock({ bodyText: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm h-20 resize-none"
              placeholder="Block content..."
            />
          </div>
        </div>
      );

    case 'banner':
      return (
        <div className="space-y-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banner Title
            </label>
            <input
              type="text"
              value={block.title}
              onChange={(e) => updateBlock({ title: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Banner title..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={block.description}
              onChange={(e) => updateBlock({ description: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm h-16 resize-none"
              placeholder="Banner description..."
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author Name
              </label>
              <input
                type="text"
                value={block.authorName}
                onChange={(e) => updateBlock({ authorName: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="Author name..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Author Image URL
              </label>
              <input
                type="url"
                value={block.authorImage}
                onChange={(e) => updateBlock({ authorImage: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="/authors/author.jpg"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Publish Date
              </label>
              <input
                type="date"
                value={block.publishDate}
                onChange={(e) => updateBlock({ publishDate: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reading Time
              </label>
              <input
                type="text"
                value={block.readingTime}
                onChange={(e) => updateBlock({ readingTime: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                placeholder="5 min read"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banner Image URL
            </label>
            <input
              type="url"
              value={block.articleImage}
              onChange={(e) => updateBlock({ articleImage: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="/banner-image.jpg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image Alt Text
            </label>
            <input
              type="text"
              value={block.articleImageAlt}
              onChange={(e) => updateBlock({ articleImageAlt: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="Banner image description..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banner Variant
            </label>
            <select
              value={block.variant || 'article'}
              onChange={(e) => updateBlock({ variant: e.target.value as 'default' | 'with-svg' | 'article' })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="default">Default</option>
              <option value="with-svg">With SVG</option>
              <option value="article">Article</option>
            </select>
          </div>
        </div>
      );

    case 'share-and-author-info':
      return (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Author Image URL
            </label>
            <input
              type="url"
              value={block.authorImageUrl || ''}
              onChange={(e) => updateBlock({ authorImageUrl: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              placeholder="/team/jonathan.png"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Editorial Info
            </label>
            <textarea
              value={block.editorialInfo || ''}
              onChange={(e) => updateBlock({ editorialInfo: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
              rows={4}
              placeholder="About the author and additional information..."
            />
          </div>
          
          <p className="text-xs text-gray-500">
            This block includes social sharing buttons and author information.
          </p>
        </div>
      );

    default:
      return <div>Unknown block type</div>;
  }
}
